import { LoadingState, QueryError } from '#/components/common/loading-state'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { BookOpen, Briefcase, ChevronDown, ChevronRight, Coins, Download, Info, ListChecks, Map as MapIcon, Sparkles } from 'lucide-react'
import { AppHeader } from '#/components/layout/app-header'
import { Button } from '#/components/ui/button'
import { Badge } from '#/components/ui/badge'
import { useWorkspacePlans } from '#/lib/api'
import { fmtHours, fmtIdr, fmtPct, fmtTokens, fmtUsd, usePlanCost } from '#/lib/api-cost'
import type { FeatureCostDto, PlanCostDto, TaskCostDto } from '#/lib/api-cost'
import { cn } from '#/lib/utils'
import { payerLabel, workflowErrorLabel } from '#/components/serve/session-card'
import { isPaidBy, payerOfPaidBy } from '#/lib/agents/policy'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/workspace/$workspaceId/cost')({
  validateSearch: z.object({ plan: z.string().optional() }),
  component: CostView,
})

type SortKey = 'cost' | 'duration'
// Orkestrator E1 (additive, read defensively): agent-team usage lands here with `source:'workflow'`,
// a per-row `stoppedBy` budget code and a plan-level `budgetStops` count. Older servers omit them → old UI.
type RowExt = TaskCostDto & { source: TaskCostDto['source'] | 'workflow'; stoppedBy?: string | null; ranOn?: { execLocation: string; paidBy: string; derived: boolean } | null }
type CostExt = PlanCostDto & { budgetStops?: number }
const stoppedByOf = (r: TaskCostDto) => (r as RowExt).stoppedBy ?? null
const hasLimitColumn = (features: FeatureCostDto[]) => features.some((f) => f.rows.some((r) => 'stoppedBy' in r))
/** Orkestrator E3 — "Dijalankan di": where the task's newest run / agent team ran and who paid (older servers: '—'). */
const ranOnLabel = (r: TaskCostDto) => {
  const o = (r as RowExt).ranOn
  if (!o || !isPaidBy(o.paidBy)) return '—'
  return m.pol_ran_on({ where: o.execLocation === 'local' ? m.pol_where_local() : m.pol_where_cloud(), payer: payerLabel(payerOfPaidBy(o.paidBy)) })
}

function CostView() {
  const { workspaceId } = Route.useParams()
  const { plan: planParam } = Route.useSearch()
  const navigate = useNavigate()
  const wsQuery = useWorkspacePlans(workspaceId)
  const { data: ws } = wsQuery
  const planId = planParam ?? [...(ws?.plans ?? [])].reverse().find((p) => p.status === 'completed')?.id ?? ws?.plans.at(-1)?.id
  useEffect(() => {
    if (!planParam && planId) navigate({ to: '/workspace/$workspaceId/cost', params: { workspaceId }, search: { plan: planId }, replace: true })
  }, [planParam, planId, workspaceId, navigate])
  const { data, isLoading, isError, isFetching, refetch } = usePlanCost(planId ?? '', Boolean(planId))
  const cycle = ws?.plans.find((p) => p.id === planId)?.cycle

  const NavBtn = ({ to, icon, label, active }: { to: 'roadmap' | 'task' | 'wiki'; icon: React.ReactNode; label: string; active?: boolean }) => (
    <Button asChild size="sm" variant={active ? 'secondary' : 'ghost'} className="h-8 gap-1.5 text-xs">
      <Link to="/workspace/$workspaceId/$view" params={{ workspaceId, view: to }} search={{ plan: planId }} aria-label={label} title={label}>{icon} <span className="hidden xl:inline">{label}</span></Link>
    </Button>
  )

  return (
    <>
      <AppHeader
        showStats={false}
        compact
        left={
          <nav className="hidden items-center gap-1 truncate text-sm text-muted-foreground lg:flex">
            <span>/</span>
            <Link to="/workspace/$workspaceId" params={{ workspaceId }} className="truncate hover:text-foreground">{ws?.workspace.name ?? '…'}</Link>
            <span>/</span>
            <span className="truncate text-foreground">{cycle ? m.ws_breadcrumb_plan({ n: cycle }) : '…'}</span>
          </nav>
        }
        right={
          <div className="flex items-center gap-1">
            <NavBtn to="roadmap" icon={<MapIcon className="size-4" />} label={m.cost_back()} />
            <NavBtn to="wiki" icon={<BookOpen className="size-4" />} label={m.roadmap_wiki()} />
            <NavBtn to="task" icon={<ListChecks className="size-4" />} label={m.roadmap_view_tasks()} />
            <Button asChild size="sm" variant="secondary" className="h-8 gap-1.5 text-xs">
              <Link to="/workspace/$workspaceId/cost" params={{ workspaceId }} search={{ plan: planId }} title={m.cost_nav()}><Coins className="size-4" /> <span className="hidden xl:inline">{m.cost_nav()}</span></Link>
            </Button>
            <Button asChild size="sm" variant="ghost" className="h-8 gap-1.5 text-xs">
              <Link to="/workspace/$workspaceId/studio" params={{ workspaceId }} title={m.cost_nav_studio()}><Briefcase className="size-4" /> <span className="hidden xl:inline">{m.cost_nav_studio()}</span></Link>
            </Button>
          </div>
        }
      />
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-6xl space-y-6 p-4 sm:p-6">
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="font-heading text-xl font-semibold">{m.cost_title()}</h1>
              <p className="text-sm text-muted-foreground">{m.cost_subtitle()}</p>
            </div>
            <Button asChild size="sm" variant="outline" className="h-8 gap-1.5" disabled={!planId}>
              <a href={`/api/plan/${planId ?? ''}/cost?format=csv`} target="_blank" rel="noreferrer"><Download className="size-4" /> {m.cost_export_csv()}</a>
            </Button>
          </header>
          {wsQuery.isError ? <QueryError onRetry={() => wsQuery.refetch()} retrying={wsQuery.isFetching} /> : isError ? <QueryError onRetry={refetch} retrying={isFetching} /> : ws && !planId ? <p className="text-sm text-muted-foreground">{m.loading_empty_workspace()}</p> : isLoading || !data ? (
            <LoadingState compact label={m.cost_loading()} />
          ) : (
            <CostBody data={data} />
          )}
        </div>
      </main>
    </>
  )
}

function CostBody({ data }: { data: PlanCostDto }) {
  const [sort, setSort] = useState<SortKey>('cost')
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set())
  const t = data.totals
  const totalCost = t.costUsd + t.generationCostUsd
  const features = useMemo(
    () => [...data.features].sort((a, b) => (sort === 'cost' ? b.costUsd - a.costUsd || b.durationMs - a.durationMs : b.durationMs - a.durationMs || b.costUsd - a.costUsd)),
    [data.features, sort],
  )
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  // no task has tokens or cost yet (duration alone still shows $0 everywhere) → explain how to turn cost telemetry on
  const noTelemetry = t.costUsd === 0 && t.tokens === 0
  const budgetStops = (data as CostExt).budgetStops
  const showStops = typeof budgetStops === 'number'
  const limitCol = hasLimitColumn(data.features)
  return (
    <>
      <div className={cn('grid gap-3 sm:grid-cols-2', showStops ? 'xl:grid-cols-5' : 'xl:grid-cols-4')}>
        <Tile label={m.cost_tile_cost()} value={fmtUsd(totalCost, t.estimated)} hint={`${fmtIdr(totalCost, t.idrRate)}${totalCost > 0 ? ' · ' : ''}${m.cost_generation()} ${fmtUsd(t.generationCostUsd)}${t.tokens > 0 ? ` · ${m.cost_tokens_n({ n: fmtTokens(t.tokens) })}` : ''}${t.estimated ? ` · ${m.cost_estimated()}` : ''}`} />
        <Tile label={m.cost_tile_hours()} value={fmtHours(t.durationMs)} hint={m.cost_tile_hours_hint()} />
        <Tile label={m.cost_tile_firstpass()} value={fmtPct(t.firstPassRate)} hint={m.cost_tile_firstpass_hint()} tone={t.firstPassRate === null ? undefined : t.firstPassRate >= 0.7 ? 'done' : 'progress'} />
        <Tile label={m.cost_tile_retries()} value={String(t.retries)} hint={m.cost_tile_retries_hint()} tone={t.retries > 0 ? 'failed' : undefined} />
        {showStops ? <Tile label={m.orx_budget_stops()} value={String(budgetStops)} hint={m.orx_budget_stops_hint()} tone={budgetStops > 0 ? 'failed' : undefined} testId="cost-budget-stops" /> : null}
      </div>

      {noTelemetry ? (
        <div className="flex gap-3 rounded-2xl border border-dashed p-4 text-sm">
          <Info className="mt-0.5 size-4 shrink-0 text-status-progress" aria-hidden="true" />
          <div className="min-w-0">
            <div className="font-heading font-semibold">{m.cost_no_telemetry_title()}</div>
            <p className="mt-0.5 text-muted-foreground">{m.cost_no_telemetry_desc()}</p>
          </div>
        </div>
      ) : t.estimated ? (
        <p className="text-xs text-muted-foreground">{m.cost_estimated_hint()}</p>
      ) : null}

      {!data.pro ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-card p-4">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 font-heading font-semibold"><Sparkles className="size-4 text-status-progress" /> {m.cost_pro_title()}</div>
            <p className="mt-0.5 text-sm text-muted-foreground">{m.cost_pro_desc()}</p>
          </div>
          <Button asChild size="sm"><Link to="/pricing" search={{ voucher: undefined }}>{m.cost_pro_cta()}</Link></Button>
        </div>
      ) : null}

      {data.pro && data.daily.length ? <Daily daily={data.daily} /> : null}

      <section className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground">{m.cost_table_title()}</h2>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <span>{m.cost_sort_by()}</span>
            {(['cost', 'duration'] as SortKey[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setSort(k)}
                aria-pressed={sort === k}
                className={cn('rounded-md px-2 py-1 hover:text-foreground', sort === k && 'bg-muted font-medium text-foreground')}
              >
                {k === 'cost' ? m.cost_sort_cost() : m.cost_sort_duration()}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto rounded-2xl border bg-card">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-semibold">{m.cost_th_feature()}</th>
                <th className="px-3 py-2 text-right font-semibold">{m.cost_th_tasks()}</th>
                <th className="px-3 py-2 text-right font-semibold">{m.cost_th_done()}</th>
                <th className="px-3 py-2 text-right font-semibold">{m.cost_th_verified()}</th>
                <th className="px-3 py-2 text-right font-semibold">{m.cost_th_firstpass()}</th>
                <th className="px-3 py-2 text-right font-semibold">{m.cost_th_retries()}</th>
                {limitCol ? <th className="px-3 py-2 text-right font-semibold">{m.orx_limit_col()}</th> : null}
                <th className="px-3 py-2 text-right font-semibold">{m.cost_th_duration()}</th>
                <th className="px-3 py-2 text-right font-semibold">{m.cost_th_cost()}</th>
              </tr>
            </thead>
            <tbody>
              {features.map((f) => (
                <FeatureRow key={f.id} f={f} pro={data.pro} maxCost={Math.max(...features.map((x) => x.costUsd), 0.000001)} open={open.has(f.id)} onToggle={() => toggle(f.id)} limitCol={limitCol} />
              ))}
            </tbody>
            <tfoot className="text-xs text-muted-foreground">
              <tr className="border-t">
                <td className="px-3 py-2" colSpan={limitCol ? 7 : 6}>
                  <span className="flex items-center gap-1.5"><span className="size-4" /> <Sparkles className="size-3.5" aria-hidden="true" /> {m.cost_generation_row()}</span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{t.generationTokens > 0 ? m.cost_tokens_n({ n: fmtTokens(t.generationTokens) }) : '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtUsd(t.generationCostUsd)}</td>
              </tr>
              <tr className="border-t font-medium text-foreground">
                <td className="px-3 py-2" colSpan={limitCol ? 7 : 6}><span className="flex items-center gap-1.5"><span className="size-4" /> {m.cost_total_row()}</span></td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtHours(t.durationMs)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtUsd(totalCost, t.estimated)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      {data.pro && data.byAgent.length ? (
        <section className="space-y-2">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground">{m.cost_agent_title()}</h2>
          <div className="overflow-x-auto rounded-2xl border bg-card">
            <table className="w-full min-w-[420px] text-sm">
              <thead>
                <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-semibold">{m.cost_th_agent_name()}</th>
                  <th className="px-3 py-2 text-right font-semibold">{m.cost_th_tasks()}</th>
                  <th className="px-3 py-2 text-right font-semibold">{m.cost_th_firstpass()}</th>
                  <th className="px-3 py-2 text-right font-semibold">{m.cost_th_duration()}</th>
                  <th className="px-3 py-2 text-right font-semibold">{m.cost_th_cost()}</th>
                </tr>
              </thead>
              <tbody>
                {data.byAgent.map((a) => (
                  <tr key={a.agent} className="border-b last:border-0">
                    <td className="px-3 py-2 font-mono text-xs">{a.agent}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{a.tasks}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtPct(a.firstPassRate)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtHours(a.durationMs)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtUsd(a.costUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      {data.byModel.length ? (
        <section className="space-y-2" data-testid="cost-by-model">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground">{m.cost_model_title()}</h2>
            <p className="text-xs text-muted-foreground">{m.cost_model_hint({ rate: t.idrRate.toLocaleString('id-ID') })}</p>
          </div>
          <div className="overflow-x-auto rounded-2xl border bg-card">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-semibold">{m.cost_th_model()}</th>
                  <th className="px-3 py-2 text-right font-semibold">{m.cost_th_tasks()}</th>
                  <th className="px-3 py-2 text-right font-semibold">{m.cost_th_tokens_in()}</th>
                  <th className="px-3 py-2 text-right font-semibold">{m.cost_th_tokens_out()}</th>
                  <th className="px-3 py-2 text-right font-semibold">{m.cost_th_cost()}</th>
                  <th className="px-3 py-2 text-right font-semibold">{m.cost_th_idr()}</th>
                </tr>
              </thead>
              <tbody>
                {data.byModel.map((r) => (
                  <tr key={r.model} className="border-b last:border-0">
                    <td className="px-3 py-2 font-mono text-xs">{r.model}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.tasks}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.tokensIn > 0 ? fmtTokens(r.tokensIn) : r.tokens > 0 ? <span title={m.cost_tokens_no_split()}>{fmtTokens(r.tokens)}*</span> : '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.tokensOut > 0 ? fmtTokens(r.tokensOut) : '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {fmtUsd(r.costUsd, r.estimated)}
                      <SourceBadge source={r.estimated ? 'estimated' : null} estimated={r.estimated} />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{fmtIdr(r.costUsd, t.idrRate) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </>
  )
}

/** Parity P4 (Zuse Tokenmaxer): say where a number comes from instead of pretending every figure is exact. */
function SourceBadge({ source, estimated }: { source: TaskCostDto['source'] | 'workflow' | string; estimated: boolean }) {
  if (!source && !estimated) return null
  const label = estimated || source === 'estimated' ? m.cost_src_estimated() : source === 'runner' ? m.cost_src_runner() : source === 'workflow' ? m.orx_source_workflow() : m.cost_src_agent()
  return <span className={cn('ml-1.5 rounded-full border px-1.5 py-px text-[10px] font-medium', estimated || source === 'estimated' ? 'border-status-progress/50 text-status-progress' : 'border-border text-muted-foreground')}>{label}</span>
}

function Tile({ label, value, hint, tone, testId }: { label: string; value: string; hint?: string; tone?: 'done' | 'progress' | 'failed'; testId?: string }) {
  return (
    <div className="rounded-2xl border bg-card p-4" data-testid={testId}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('mt-1 font-heading text-2xl font-semibold tabular-nums', tone === 'done' && 'text-status-done', tone === 'progress' && 'text-status-progress', tone === 'failed' && 'text-status-failed')}>{value}</div>
      {hint ? <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div> : null}
    </div>
  )
}

function FeatureRow({ f, pro, maxCost, open, onToggle, limitCol }: { f: FeatureCostDto; pro: boolean; maxCost: number; open: boolean; onToggle: () => void; limitCol: boolean }) {
  const canOpen = pro && f.rows.length > 0
  const stopped = f.rows.filter((r) => stoppedByOf(r)).length
  return (
    <Fragment>
      <tr className={cn('border-b last:border-0', canOpen && 'cursor-pointer hover:bg-muted/40')} onClick={canOpen ? onToggle : undefined}>
        <td className="px-3 py-2">
          <div className="flex items-center gap-1.5">
            {canOpen ? (
              <button type="button" className="text-muted-foreground" aria-label={open ? m.cost_collapse() : m.cost_expand()} onClick={(e) => { e.stopPropagation(); onToggle() }}>
                {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
              </button>
            ) : (
              <span className="size-4" />
            )}
            <span className="truncate font-medium" title={f.name}>{f.name}</span>
            <Badge variant="outline" className="shrink-0 text-[10px]">{m.cost_phase({ n: f.phase })}</Badge>
          </div>
        </td>
        <td className="px-3 py-2 text-right tabular-nums">{f.tasks}</td>
        <td className="px-3 py-2 text-right tabular-nums">{f.done}</td>
        <td className="px-3 py-2 text-right tabular-nums text-status-done">{f.verified}</td>
        <td className="px-3 py-2 text-right tabular-nums">{fmtPct(f.firstPassRate)}</td>
        <td className={cn('px-3 py-2 text-right tabular-nums', f.retries > 0 && 'text-status-failed')}>{f.retries}</td>
        {limitCol ? <td className={cn('px-3 py-2 text-right tabular-nums', stopped > 0 && 'text-status-failed')}>{stopped > 0 ? stopped : '—'}</td> : null}
        <td className="px-3 py-2 text-right tabular-nums">{fmtHours(f.durationMs)}</td>
        <td className="px-3 py-2 text-right tabular-nums">
          <span className="inline-flex items-center gap-2">
            {f.costUsd > 0 ? <span aria-hidden="true" className="hidden h-1.5 rounded-full bg-status-progress/70 sm:inline-block" style={{ width: `${Math.max(2, Math.round((f.costUsd / maxCost) * 56))}px` }} /> : null}
            {fmtUsd(f.costUsd, f.estimated)}
          </span>
        </td>
      </tr>
      {open ? (
        <tr className="border-b last:border-0 bg-muted/20">
          <td colSpan={limitCol ? 9 : 8} className="px-3 py-2">
            {f.rows.length ? (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                    <th className="py-1 pr-3 font-semibold">{m.cost_th_ref()}</th>
                    <th className="py-1 pr-3 font-semibold">{m.cost_th_tier()}</th>
                    <th className="py-1 pr-3 text-right font-semibold">{m.cost_th_attempts()}</th>
                    <th className="py-1 pr-3 text-right font-semibold">{m.cost_th_duration()}</th>
                    <th className="py-1 pr-3 text-right font-semibold">{m.cost_th_tokens_in()}</th>
                    <th className="py-1 pr-3 text-right font-semibold">{m.cost_th_tokens_out()}</th>
                    <th className="py-1 pr-3 text-right font-semibold">{m.cost_th_cost()}</th>
                    {limitCol ? <th className="py-1 pr-3 font-semibold">{m.orx_limit_col()}</th> : null}
                    <th className="py-1 pr-3 font-semibold">{m.cost_th_agent()}</th>
                    <th className="py-1 pr-3 font-semibold">{m.pol_ran_on_col()}</th>
                    <th className="py-1 font-semibold">{m.cost_th_verification()}</th>
                  </tr>
                </thead>
                <tbody>
                  {f.rows.map((r) => (
                    <tr key={r.id} className="border-t border-border/60">
                      <td className="py-1 pr-3 font-mono">{r.ref}</td>
                      <td className="py-1 pr-3">{r.tier}</td>
                      <td className="py-1 pr-3 text-right tabular-nums">{r.attempts}{r.retries > 0 ? <span className="text-status-failed"> ({r.retries})</span> : null}</td>
                      <td className="py-1 pr-3 text-right tabular-nums">{fmtHours(r.durationMs)}</td>
                      <td className="py-1 pr-3 text-right tabular-nums">{r.tokensIn > 0 ? fmtTokens(r.tokensIn) : r.tokens > 0 ? <span title={m.cost_tokens_no_split()}>{fmtTokens(r.tokens)}*</span> : '—'}</td>
                      <td className="py-1 pr-3 text-right tabular-nums">{r.tokensOut > 0 ? fmtTokens(r.tokensOut) : '—'}</td>
                      <td className="py-1 pr-3 text-right tabular-nums" title={r.tokens > 0 ? m.cost_tokens_n({ n: fmtTokens(r.tokens) }) : undefined}>
                        {fmtUsd(r.costUsd, r.estimated)}
                        <SourceBadge source={r.source} estimated={r.estimated} />
                      </td>
                      {limitCol ? <td className="py-1 pr-3 text-status-failed" data-testid="cost-stopped-by">{workflowErrorLabel(stoppedByOf(r)) ?? <span className="text-muted-foreground">—</span>}</td> : null}
                      <td className="py-1 pr-3 truncate text-muted-foreground">{[r.agent, r.model].filter(Boolean).join(' · ') || '—'}</td>
                      <td className="py-1 pr-3 text-muted-foreground" data-testid="cost-ran-on">{ranOnLabel(r)}</td>
                      <td className={cn('py-1', r.verification === 'verified' ? 'text-status-done' : r.verification === 'override' ? 'text-status-progress' : r.verification === 'failed' ? 'text-status-failed' : 'text-muted-foreground')}>{r.verification}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-xs text-muted-foreground">{m.cost_no_rows()}</p>
            )}
          </td>
        </tr>
      ) : null}
    </Fragment>
  )
}

/**
 * Two small multiples rather than one dual-axis chart: cost and verified count are different measures,
 * so each gets its own baseline. Pure inline SVG, semantic colours only.
 */
function Daily({ daily }: { daily: { day: string; costUsd: number; durationMs: number; verified: number }[] }) {
  const W = 600
  const H = 56
  const maxCost = Math.max(...daily.map((d) => d.costUsd), 0.000001)
  const maxVer = Math.max(...daily.map((d) => d.verified), 1)
  const x = (i: number) => (daily.length === 1 ? W / 2 : (i / (daily.length - 1)) * W)
  const line = daily.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${(H - (d.costUsd / maxCost) * (H - 6) - 3).toFixed(1)}`).join(' ')
  const area = `${line} L${x(daily.length - 1).toFixed(1)},${H} L${x(0).toFixed(1)},${H} Z`
  const bw = Math.max(2, Math.min(14, W / Math.max(daily.length, 1) - 3))
  return (
    <section className="grid gap-3 rounded-2xl border bg-card p-4 sm:grid-cols-2">
      <figure className="m-0 min-w-0">
        <figcaption className="mb-1 flex items-baseline justify-between text-xs text-muted-foreground">
          <span>{m.cost_daily_cost()}</span>
          <span className="tabular-nums">{fmtUsd(maxCost)}</span>
        </figcaption>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-14 w-full" role="img" aria-label={`${m.cost_daily_title()} — ${m.cost_daily_cost()}`}>
          {daily.length > 1 ? <path d={area} fill="var(--status-progress)" opacity="0.14" /> : null}
          <path d={line} fill="none" stroke="var(--status-progress)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          {daily.map((d, i) => (
            <circle key={d.day} cx={x(i)} cy={H - (d.costUsd / maxCost) * (H - 6) - 3} r="2.5" fill="var(--status-progress)" vectorEffect="non-scaling-stroke" />
          ))}
        </svg>
      </figure>
      <figure className="m-0 min-w-0">
        <figcaption className="mb-1 flex items-baseline justify-between text-xs text-muted-foreground">
          <span>{m.cost_daily_verified()}</span>
          <span className="tabular-nums">{maxVer}</span>
        </figcaption>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-14 w-full" role="img" aria-label={`${m.cost_daily_title()} — ${m.cost_daily_verified()}`}>
          {daily.map((d, i) => {
            const h = Math.max(d.verified > 0 ? 3 : 0, (d.verified / maxVer) * (H - 4))
            return <rect key={d.day} x={Math.max(0, x(i) - bw / 2)} y={H - h} width={bw} height={h} rx="2" fill="var(--status-done)" />
          })}
        </svg>
      </figure>
      <div className="col-span-full flex justify-between text-[10px] tabular-nums text-muted-foreground">
        <span>{daily[0]?.day}</span>
        <span>{daily.at(-1)?.day}</span>
      </div>
    </section>
  )
}
